import i18n from "i18n"

export const getPartSelectionKey = part =>
  `${part?.cert?.certification_id ?? "no-cert"}:${part?.partidaId ?? "no-part"}`

export const roundAmount = value =>
  Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100

export const collectLeafParts = chapter => {
  if (!chapter) return []

  const leafPartsInChapter = chapter.parts || []
  const leafPartsInChildren = (chapter.chapters || []).reduce(
    (acc, childChapter) => {
      acc.push(...collectLeafParts(childChapter))
      return acc
    },
    [],
  )

  return [...leafPartsInChapter, ...leafPartsInChildren]
}

export const getSelectedPartAmount = (part, invoicingDrafts) => {
  const certificationId = part?.cert?.certification_id
  const partId = part?.partidaId

  return roundAmount(
    invoicingDrafts?.[certificationId]?.[partId]?.invoicing ??
      part?.invoicing ??
      0,
  )
}

const buildCertificationPart = (part, invoicingDrafts) => {
  const certId = part?.cert?.certification_id
  const partidaId = part?.partidaId
  const projectId = part?.projectId
  if (!certId || !partidaId) return null

  return {
    projectId: projectId ?? null,
    certId,
    partidaId,
    amount: getSelectedPartAmount(part, invoicingDrafts),
  }
}

export const collectSelectedLeafParts = (chapter, selectedParts) => {
  if (!chapter) return []

  return collectLeafParts(chapter).filter(
    part => selectedParts[getPartSelectionKey(part)],
  )
}

export const getChapterSelectionState = (chapter, selectedParts) => {
  const leafParts = collectLeafParts(chapter)

  if (leafParts.length === 0) {
    return {
      checked: false,
      indeterminate: false,
      hasLeafParts: false,
    }
  }

  const selectedLeafPartsCount = leafParts.reduce(
    (count, part) => count + (selectedParts[getPartSelectionKey(part)] ? 1 : 0),
    0,
  )

  return {
    checked: selectedLeafPartsCount === leafParts.length,
    indeterminate:
      selectedLeafPartsCount > 0 && selectedLeafPartsCount < leafParts.length,
    hasLeafParts: true,
  }
}

export const getSelectedInvoiceParts = (certHistoryTree, selectedParts) =>
  (certHistoryTree?.certs || []).reduce((acc, cert, certIndex) => {
    const certNumber = certIndex + 1
    ;(cert.chapters || []).forEach(chapter => {
      collectSelectedLeafParts(chapter, selectedParts).forEach(part => {
        acc.push({ ...part, certNumber })
      })
    })
    return acc
  }, [])

export const buildPrefilledInvoiceConcepts = (
  selectedInvoiceParts,
  invoicingDrafts,
) =>
  selectedInvoiceParts
    .map((part, index) => {
      const partDescription = part.name || part.description || ""
      const description = part.certNumber
        ? i18n.t("certHistory.invoiceDraft.conceptDescription", {
            certNumber: part.certNumber,
            description: partDescription,
          })
        : partDescription
      const certificationPart = buildCertificationPart(part, invoicingDrafts)
      if (!certificationPart) return null

      return {
        description,
        quantity: 1,
        price: getSelectedPartAmount(part, invoicingDrafts),
        taxes: [],
        discount: 0,
        position: index,
        certsAssociated: [certificationPart],
      }
    })
    .filter(Boolean)

export const buildGroupedInvoiceConcept = (
  selectedInvoiceParts,
  invoicingDrafts,
  totalInvoicing,
) => [
  {
    description: i18n.t("certHistory.invoiceDraft.groupedConceptDescription"),
    quantity: 1,
    price: roundAmount(totalInvoicing),
    taxes: [],
    discount: 0,
    position: 0,
    certsAssociated: selectedInvoiceParts
      .map(part => buildCertificationPart(part, invoicingDrafts))
      .filter(Boolean),
  },
]

export const buildCertificationParts = (
  selectedInvoiceParts,
  invoicingDrafts,
) =>
  selectedInvoiceParts
    .map(part => buildCertificationPart(part, invoicingDrafts))
    .filter(Boolean)
